import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Circle } from "lucide-react";
import {
  PrivacyPermissionsForm,
  PrivacyPermissionsFormValue,
  emptyPppcFormValue,
  validatePppcForm,
} from "./PrivacyPermissionsForm";
import { useSavePppcPolicy } from "@/hooks/usePppcPolicy";
import { PppcPolicySnapshot } from "@/types/pppc";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";

interface PrivacyPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appName: string;
  bundleId: string;
  existing?: PppcPolicySnapshot | null;
}

export const PrivacyPermissionsDialog = ({
  open,
  onOpenChange,
  appName,
  bundleId,
  existing,
}: PrivacyPermissionsDialogProps) => {
  const baseline = useMemo<PrivacyPermissionsFormValue>(
    () => ({
      codeRequirement: existing?.codeRequirement ?? "",
      choices: { ...(existing?.choices ?? {}) },
    }),
    [existing],
  );

  const [value, setValue] = useState<PrivacyPermissionsFormValue>(baseline);
  const [errors, setErrors] = useState<ReturnType<typeof validatePppcForm>>({});
  const save = useSavePppcPolicy(bundleId);

  useEffect(() => {
    if (open) {
      setValue(baseline);
      setErrors({});
    }
  }, [open, baseline]);

  const guard = useUnsavedChangesGuard({
    open,
    current: value,
    baseline,
    onClose: () => onOpenChange(false),
    onDiscard: () => {
      setValue(baseline);
      setErrors({});
    },
    surface: "PrivacyPermissionsDialog",
    isSaving: save.isPending,
  });

  const handleSave = async () => {
    const v = validatePppcForm(value);
    setErrors(v);
    if (v.codeRequirement || (v.serviceIssues && v.serviceIssues.length > 0)) return;

    await save.mutateAsync({
      existingPolicyId: existing?.policyId,
      appName,
      bundleId,
      codeRequirement: value.codeRequirement,
      choices: value.choices,
    });
    guard.markClean();
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={guard.requestClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>Configure Privacy Permissions for {appName}</DialogTitle>
              {guard.isDirty && (
                <Badge
                  variant="outline"
                  className="border-warning text-warning gap-1.5 font-normal shrink-0"
                >
                  <Circle className="h-2 w-2 fill-warning text-warning" />
                  Unsaved changes
                </Badge>
              )}
            </div>
            <DialogDescription>
              Pre-approve or deny this app's access to macOS privacy services to avoid
              end-user prompts.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            <PrivacyPermissionsForm
              bundleId={bundleId}
              value={value}
              onChange={setValue}
              errors={errors}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Cancel always reverts to the saved baseline so the dialog
                // never closes (or reopens) in a dirty state.
                setValue(baseline);
                setErrors({});
                onOpenChange(false);
              }}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={save.isPending || !guard.isDirty}
              title={!guard.isDirty ? "No changes to save" : undefined}
            >
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={guard.confirmOpen}
        onCancel={guard.cancelDiscard}
        onDiscard={guard.confirmDiscard}
        isSaving={guard.isSaving}
      />
    </>
  );
};
